import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Skeleton,
  Divider,
  Button,
} from '@mui/material';
import {
  AccountBalance as LoanIcon,
  Description as ApplicationIcon,
  Payment as PaymentIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  RecentLoan,
  RecentApplication,
  RecentPayment,
} from '../../../api/types/dashboard.types';
import { LoanStatus, ApplicationStatus } from '../../../api/types/common.types';

interface RecentActivitiesProps {
  loans?: RecentLoan[];
  applications?: RecentApplication[];
  payments?: RecentPayment[];
  isLoading?: boolean;
}

const getStatusColor = (
  status: LoanStatus | ApplicationStatus
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  const statusMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    ACTIVE: 'success',
    PAID: 'success',
    OVERDUE: 'error',
    DEFAULTED: 'error',
    PENDING: 'warning',
    APPROVED: 'info',
    REJECTED: 'error',
    DISBURSED: 'success',
    CANCELLED: 'default',
  };
  return statusMap[status] || 'default';
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

const RecentActivities: React.FC<RecentActivitiesProps> = ({
  loans = [],
  applications = [],
  payments = [],
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={48} sx={{ mb: 2 }} />
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  }

  const renderLoansList = () => (
    <List>
      {loans.length === 0 ? (
        <ListItem>
          <ListItemText
            primary="No recent loans"
            secondary="Recent loan activities will appear here"
          />
        </ListItem>
      ) : (
        loans.slice(0, 5).map((loan, index) => (
          <React.Fragment key={loan.loanId}>
            <ListItem
              sx={{
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              onClick={() => navigate(`/loans/active?loanId=${loan.loanId}`)}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <LoanIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={500}>
                      {loan.customerName}
                    </Typography>
                    <Chip
                      label={loan.status}
                      size="small"
                      color={getStatusColor(loan.status)}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(loan.amount)} • Disbursed:{' '}
                      {format(new Date(loan.disbursementDate), 'MMM dd, yyyy')}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            {index < loans.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))
      )}
    </List>
  );

  const renderApplicationsList = () => (
    <List>
      {applications.length === 0 ? (
        <ListItem>
          <ListItemText
            primary="No recent applications"
            secondary="Recent loan applications will appear here"
          />
        </ListItem>
      ) : (
        applications.slice(0, 5).map((app, index) => (
          <React.Fragment key={app.applicationId}>
            <ListItem
              sx={{
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              onClick={() => navigate(`/loans/applications?appId=${app.applicationId}`)}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                  <ApplicationIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={500}>
                      {app.customerName}
                    </Typography>
                    <Chip
                      label={app.status}
                      size="small"
                      color={getStatusColor(app.status)}
                    />
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(app.amount)} • Applied:{' '}
                    {format(new Date(app.appliedDate), 'MMM dd, yyyy')}
                  </Typography>
                }
              />
            </ListItem>
            {index < applications.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))
      )}
    </List>
  );

  const renderPaymentsList = () => (
    <List>
      {payments.length === 0 ? (
        <ListItem>
          <ListItemText
            primary="No recent payments"
            secondary="Recent payment activities will appear here"
          />
        </ListItem>
      ) : (
        payments.slice(0, 5).map((payment, index) => (
          <React.Fragment key={payment.paymentId}>
            <ListItem
              sx={{
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              onClick={() => navigate(`/payments?paymentId=${payment.paymentId}`)}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <PaymentIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={500}>
                      {payment.customerName}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {formatCurrency(payment.amount)}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    {payment.method} • {format(new Date(payment.paymentDate), 'MMM dd, yyyy')}
                  </Typography>
                }
              />
            </ListItem>
            {index < payments.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))
      )}
    </List>
  );

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Recent Activities
          </Typography>
        </Box>

        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label={`Loans (${loans.length})`} />
          <Tab label={`Applications (${applications.length})`} />
          <Tab label={`Payments (${payments.length})`} />
        </Tabs>

        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {activeTab === 0 && renderLoansList()}
          {activeTab === 1 && renderApplicationsList()}
          {activeTab === 2 && renderPaymentsList()}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            endIcon={<ArrowForwardIcon />}
            onClick={() => {
              if (activeTab === 0) navigate('/loans/active');
              else if (activeTab === 1) navigate('/loans/applications');
              else navigate('/payments');
            }}
          >
            View All
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RecentActivities;
